import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'

import App from 'containers/App'
import EULAModal from 'containers/EULAModal'
import FirstLoginModal from 'containers/FirstLoginModal'
import GlobalBanner from 'containers/GlobalBanner'
import Login from 'containers/Login'
import Navigation from 'containers/Navigation'
import NewRelease from 'containers/NewRelease'
import Reintegration from 'containers/Reintegration'
import ToastsContainer from 'containers/Toasts'

import createReducer from '../reducer'

import TooltipProvider from './context/TooltipContext'
import KpiPage from './pages/KpiPage'
import Placeholder from './pages/Placeholder'
import RefreshErrorScreen from './pages/RefreshErrorScreen'
import styledTheme from './styles/styledTheme'

const handleChunkLoadError = () => {
  window.DISABLE_SENTRY = true
  const container = document.getElementById('app')
  const root = createRoot(container)

  root.render(<RefreshErrorScreen />)
}

const Activities = React.lazy(() => import('../containers/Activities').catch(handleChunkLoadError))
const AwarenessCenter = React.lazy(() =>
  import('../containers/AwarenessCenter').catch(handleChunkLoadError)
)
const Dashboard = React.lazy(() => import('../containers/Dashboard').catch(handleChunkLoadError))
const Mailboxes = React.lazy(() => import('../containers/Mailboxes').catch(handleChunkLoadError))
const Partner = React.lazy(() => import('../containers/Partner').catch(handleChunkLoadError))
const ResolutionCenter = React.lazy(() =>
  import('../containers/ResolutionCenter').catch(handleChunkLoadError)
)
const Resources = React.lazy(() => import('../containers/Resources').catch(handleChunkLoadError))
const Settings = React.lazy(() => import('../containers/Settings').catch(handleChunkLoadError))
const GroupSettings = React.lazy(() =>
  import('../containers/GroupSettings').catch(handleChunkLoadError)
)
const ThreatLog = React.lazy(() =>
  import('../containers/Investigation').catch(handleChunkLoadError)
)
const DMARC = React.lazy(() => import('../containers/DMARC').catch(handleChunkLoadError))

require('./styles/antd.less')
require('./styles/root.styl')

let activeComponent

const renderComponentWithDataLayer =
  (store) =>
  (Component, dataLayerDependencies, componentProps = {}) =>
  (props) => {
    if (!activeComponent || activeComponent !== Component) {
      activeComponent = Component

      const reducers = {}
      dataLayerDependencies.forEach((dependency) => {
        const nextReducer = require(`../containers/${dependency}/reducer`).default
        reducers[dependency.toLowerCase()] = nextReducer()(undefined, {})
      })
      store.replaceReducer(createReducer(reducers))
    }

    const mixedProps = { ...props, ...componentProps }

    return <Component {...mixedProps} />
  }

const RootContainer = ({ store }) => {
  const render = renderComponentWithDataLayer(store)
  return (
    <ThemeProvider theme={styledTheme}>
      <TooltipProvider>
        <BrowserRouter>
          <Switch>
            <Route path={URL_CONST.login.signin} component={render(Login, ['Login'])} />
            <Route path="*">
              <App>
                <GlobalBanner>
                  <NewRelease />
                  <Reintegration />
                  <Navigation />
                  <FirstLoginModal />
                  <EULAModal />

                  <React.Suspense fallback={<Placeholder />}>
                    <Switch>
                      <Route
                        path={URL_CONST.activities.self}
                        component={render(Activities, ['Activities'])}
                      />
                      <Route
                        path={URL_CONST.resources.self}
                        component={render(Resources, ['Resources'])}
                      />
                      <Route
                        path={URL_CONST.investigation.threatLog}
                        component={render(ThreatLog, ['Investigation', 'Partner'])}
                      />
                      <Route
                        path={URL_CONST.dashboard.home}
                        component={render(Dashboard, ['Dashboard'])}
                      />
                      <Route
                        path={URL_CONST.mailboxes.self}
                        component={render(Mailboxes, ['Mailboxes', 'CustomizedAutoSync'])}
                      />
                      <Route
                        path={URL_CONST.incidents.self}
                        component={render(ResolutionCenter, ['ResolutionCenter'])}
                      />
                      <Route
                        path={URL_CONST.awarenessCenter.self}
                        component={render(AwarenessCenter, ['AwarenessCenter'], { store })}
                      />
                      <Redirect from={URL_CONST.incidents.partners} to={URL_CONST.incidents.self} />
                      <Route
                        path={URL_CONST.partners.self}
                        component={render(Partner, ['Partner'])}
                      />
                      <Route
                        path={URL_CONST.settings.self}
                        component={render(Settings, [
                          'Settings',
                          'CustomizedAutoSync',
                          'GroupSettings',
                        ])}
                      />
                      <Route path={URL_CONST.kpi.self} component={KpiPage} />
                      <Route
                        path={URL_CONST.groupSettings.self}
                        component={render(GroupSettings, ['GroupSettings', 'Settings'])}
                      />
                      <Route
                        path={URL_CONST.dmarc.self}
                        component={render(DMARC, ['DMARC'])}
                      />
                    </Switch>
                  </React.Suspense>
                </GlobalBanner>
              </App>
            </Route>
          </Switch>
          <ToastsContainer />
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  )
}

export default RootContainer
